import type {
  JsProbeDefinition,
  ProbeDefinition,
  ProbeGroupDefinition,
  WatProbeDefinition,
  WabtFeatureName
} from "./probe_types";

const DEFAULT_ASYNC_TIMEOUT_MS = 3_000;
const DEFAULT_SYNC_TIMEOUT_MS = 250;

/**
 * Canonical schema version for probe reports.
 */
export const PROBE_SCHEMA_VERSION: number = 1;

/**
 * Probe group display metadata.
 */
export const PROBE_GROUPS: readonly ProbeGroupDefinition[] = [
  {
    id: "smoke",
    title: "Smoke",
    summary: "Minimal end-to-end checks for the very first probe run."
  },
  {
    id: "wasm_feature",
    title: "WASM Features",
    summary: "WASM proposal support and related runtime gates."
  },
  {
    id: "storage",
    title: "Storage",
    summary: "Persistent storage and file access capabilities."
  },
  {
    id: "networking",
    title: "Networking",
    summary: "Browser networking primitives relevant to Poly."
  },
  {
    id: "threading",
    title: "Threading",
    summary: "Workers, scheduling, and zero-copy concurrency primitives."
  },
  {
    id: "crypto",
    title: "Crypto",
    summary: "Browser cryptography and key-generation primitives."
  },
  {
    id: "encoding",
    title: "Encoding",
    summary: "Text encoding and clone primitives used around WASM."
  },
  {
    id: "platform",
    title: "Platform",
    summary: "Host-browser APIs that shape runtime behavior."
  },
  {
    id: "performance",
    title: "Performance",
    summary: "Observability, timers, and memory measurement surfaces."
  },
  {
    id: "memory",
    title: "Memory",
    summary: "Memory sizing and modern ArrayBuffer capabilities."
  },
  {
    id: "graphics",
    title: "Graphics",
    summary: "Rendering and GPU acceleration capabilities."
  },
  {
    id: "auth",
    title: "Auth",
    summary: "Credential and passkey platform support."
  },
  {
    id: "media",
    title: "Media",
    summary: "Raw codec APIs for audio and video access."
  },
  {
    id: "transfer",
    title: "Transfer",
    summary: "Structured clone and transferable object behavior."
  }
] as const;

interface JsProbeOptions {
  readonly default_enabled?: boolean;
  readonly execution_kind: JsProbeDefinition["execution_kind"];
  readonly group_id: JsProbeDefinition["group_id"];
  readonly id: string;
  readonly requires_cross_origin_isolation?: boolean;
  readonly smoke_enabled?: boolean;
  readonly summary: string;
  readonly timeout_ms?: number;
  readonly title: string;
}

interface WatProbeOptions {
  readonly default_enabled?: boolean;
  readonly execution_kind: WatProbeDefinition["execution_kind"];
  readonly group_id: WatProbeDefinition["group_id"];
  readonly id: string;
  readonly requires_cross_origin_isolation?: boolean;
  readonly smoke_enabled?: boolean;
  readonly summary: string;
  readonly timeout_ms?: number;
  readonly title: string;
  readonly wabt_features: readonly WabtFeatureName[];
  readonly wat_source: string;
}

function define_js_probe(options: JsProbeOptions): JsProbeDefinition {
  return {
    id: options.id,
    title: options.title,
    summary: options.summary,
    group_id: options.group_id,
    execution_kind: options.execution_kind,
    default_enabled: options.default_enabled ?? true,
    smoke_enabled: options.smoke_enabled ?? false,
    timeout_ms: options.timeout_ms ?? DEFAULT_ASYNC_TIMEOUT_MS,
    requires_cross_origin_isolation:
      options.requires_cross_origin_isolation ?? false,
    implementation_kind: "javascript"
  };
}

function define_wat_probe(options: WatProbeOptions): WatProbeDefinition {
  return {
    id: options.id,
    title: options.title,
    summary: options.summary,
    group_id: options.group_id,
    execution_kind: options.execution_kind,
    default_enabled: options.default_enabled ?? true,
    smoke_enabled: options.smoke_enabled ?? false,
    timeout_ms: options.timeout_ms ?? DEFAULT_SYNC_TIMEOUT_MS,
    requires_cross_origin_isolation:
      options.requires_cross_origin_isolation ?? false,
    implementation_kind: "wat_validate",
    wabt_features: options.wabt_features,
    wat_source: options.wat_source
  };
}

/**
 * Minimal smoke probes used to validate the system end to end.
 */
export const SMOKE_PROBES: readonly ProbeDefinition[] = [
  define_js_probe({
    id: "javascript_runtime",
    title: "JavaScript Runtime",
    summary: "Confirms that page JavaScript executed at all.",
    group_id: "smoke",
    execution_kind: "sync",
    timeout_ms: DEFAULT_SYNC_TIMEOUT_MS,
    smoke_enabled: true
  }),
  define_js_probe({
    id: "cross_origin_isolated",
    title: "Cross-Origin Isolation",
    summary: "Checks whether COOP/COEP isolation is active on the page.",
    group_id: "smoke",
    execution_kind: "sync",
    timeout_ms: DEFAULT_SYNC_TIMEOUT_MS,
    smoke_enabled: true
  }),
  define_wat_probe({
    id: "webassembly_baseline_validate",
    title: "WebAssembly Baseline",
    summary: "Validates a minimal empty wasm module.",
    group_id: "smoke",
    execution_kind: "sync",
    timeout_ms: DEFAULT_SYNC_TIMEOUT_MS,
    smoke_enabled: true,
    wabt_features: [],
    wat_source: `(module)`
  }),
  define_js_probe({
    id: "shared_array_buffer_constructible",
    title: "SharedArrayBuffer Constructible",
    summary: "Checks whether SharedArrayBuffer can actually be constructed.",
    group_id: "smoke",
    execution_kind: "sync",
    timeout_ms: DEFAULT_SYNC_TIMEOUT_MS,
    smoke_enabled: true,
    requires_cross_origin_isolation: true
  })
] as const;

/**
 * WAT-backed WebAssembly proposal probes.
 */
export const WAT_PROBES: readonly ProbeDefinition[] = [
  define_wat_probe({
    id: "atomics",
    title: "Atomics",
    summary: "Validates atomic memory operations for threaded wasm.",
    group_id: "wasm_feature",
    execution_kind: "sync",
    requires_cross_origin_isolation: true,
    wabt_features: ["threads"],
    wat_source: `(module
  (memory 1 1 shared)
  (func
    i32.const 0
    i32.atomic.load
    drop
  )
)`
  }),
  define_wat_probe({
    id: "shared_memory",
    title: "Shared Memory",
    summary: "Validates shared linear memory declarations.",
    group_id: "wasm_feature",
    execution_kind: "sync",
    requires_cross_origin_isolation: true,
    wabt_features: ["threads"],
    wat_source: `(module
  (memory 1 1 shared)
)`
  }),
  define_wat_probe({
    id: "bulk_memory",
    title: "Bulk Memory",
    summary: "Validates bulk memory instructions such as memory.fill.",
    group_id: "wasm_feature",
    execution_kind: "sync",
    wabt_features: ["bulk_memory"],
    wat_source: `(module
  (memory 1)
  (func
    i32.const 0
    i32.const 0
    i32.const 0
    memory.fill
  )
)`
  }),
  define_wat_probe({
    id: "multi_value",
    title: "Multi-Value",
    summary: "Validates functions that return multiple values.",
    group_id: "wasm_feature",
    execution_kind: "sync",
    wabt_features: ["multi_value"],
    wat_source: `(module
  (func (result i32 i32)
    i32.const 0
    i32.const 0
  )
)`
  }),
  define_wat_probe({
    id: "nontrapping_fptoint",
    title: "Non-Trapping Float To Int",
    summary: "Validates saturating float-to-int conversion instructions.",
    group_id: "wasm_feature",
    execution_kind: "sync",
    wabt_features: ["sat_float_to_int"],
    wat_source: `(module
  (func (result i32)
    f32.const 0
    i32.trunc_sat_f32_s
  )
)`
  }),
  define_wat_probe({
    id: "sign_extension_ops",
    title: "Sign Extension Ops",
    summary: "Validates sign-extension operators such as i32.extend8_s.",
    group_id: "wasm_feature",
    execution_kind: "sync",
    wabt_features: ["sign_extension"],
    wat_source: `(module
  (func (result i32)
    i32.const 0
    i32.extend8_s
  )
)`
  }),
  define_wat_probe({
    id: "memory64",
    title: "Memory64",
    summary: "Validates 64-bit memory declarations.",
    group_id: "wasm_feature",
    execution_kind: "sync",
    wabt_features: ["memory64"],
    wat_source: `(module
  (memory i64 1)
)`
  }),
  define_wat_probe({
    id: "extended_const",
    title: "Extended Const",
    summary: "Validates arithmetic inside global initializers.",
    group_id: "wasm_feature",
    execution_kind: "sync",
    wabt_features: ["extended_const"],
    wat_source: `(module
  (global i32
    i32.const 1
    i32.const 2
    i32.add
  )
)`
  }),
  define_wat_probe({
    id: "mutable_globals",
    title: "Mutable Globals",
    summary: "Validates exported mutable globals.",
    group_id: "wasm_feature",
    execution_kind: "sync",
    wabt_features: ["mutable_globals"],
    wat_source: `(module
  (global (export "g") (mut i32) (i32.const 0))
)`
  }),
  define_wat_probe({
    id: "simd128",
    title: "SIMD128",
    summary: "Validates fixed-width SIMD instructions.",
    group_id: "wasm_feature",
    execution_kind: "sync",
    wabt_features: ["simd"],
    wat_source: `(module
  (func (result v128)
    v128.const i32x4 0 0 0 0
  )
)`
  }),
  define_wat_probe({
    id: "relaxed_simd",
    title: "Relaxed SIMD",
    summary: "Validates relaxed SIMD instructions.",
    group_id: "wasm_feature",
    execution_kind: "sync",
    wabt_features: ["relaxed_simd", "simd"],
    wat_source: `(module
  (func (param v128) (result v128)
    local.get 0
    i32x4.relaxed_trunc_f32x4_s
  )
)`
  }),
  define_wat_probe({
    id: "reference_types",
    title: "Reference Types",
    summary: "Validates externref parameters.",
    group_id: "wasm_feature",
    execution_kind: "sync",
    wabt_features: ["reference_types"],
    wat_source: `(module
  (func (param externref)
    local.get 0
    drop
  )
)`
  }),
  define_wat_probe({
    id: "tail_call",
    title: "Tail Call",
    summary: "Validates return_call support.",
    group_id: "wasm_feature",
    execution_kind: "sync",
    wabt_features: ["tail_call"],
    wat_source: `(module
  (func $f
    return_call $f
  )
)`
  }),
  define_wat_probe({
    id: "exception_handling",
    title: "Exception Handling",
    summary: "Validates wasm exception tags and try/catch blocks.",
    group_id: "wasm_feature",
    execution_kind: "sync",
    wabt_features: ["exceptions"],
    wat_source: `(module
  (tag $e (param i32))
  (func
    try
      i32.const 0
      throw $e
    catch $e
    end
  )
)`
  }),
  define_wat_probe({
    id: "multi_memory",
    title: "Multi-Memory",
    summary: "Validates multiple linear memories in a single module.",
    group_id: "wasm_feature",
    execution_kind: "sync",
    wabt_features: ["multi_memory"],
    wat_source: `(module
  (memory $a 1)
  (memory $b 1)
  (func
    i32.const 0
    i32.load $a
    drop
  )
)`
  }),
  define_wat_probe({
    id: "typed_function_references",
    title: "Typed Function References",
    summary: "Validates call_ref and ref.func support.",
    group_id: "wasm_feature",
    execution_kind: "sync",
    wabt_features: ["function_references"],
    wat_source: `(module
  (type $ft (func (param i32) (result i32)))
  (func $f (type $ft) (param $x i32) (result i32)
    local.get $x
  )
  (func (result i32)
    i32.const 42
    ref.func $f
    call_ref
  )
)`
  }),
  define_wat_probe({
    id: "gc",
    title: "Garbage Collection",
    summary: "Validates struct types from the GC proposal.",
    group_id: "wasm_feature",
    execution_kind: "sync",
    default_enabled: false,
    wabt_features: ["gc"],
    wat_source: `(module
  (type $point (struct (field i32) (field i32)))
  (func
    i32.const 1
    i32.const 2
    struct.new $point
    drop
  )
)`
  })
] as const;

/**
 * Pure JavaScript probes sourced from the POC and extension notes.
 */
export const JS_PROBES: readonly ProbeDefinition[] = [
  define_js_probe({
    id: "big_int_integration",
    title: "BigInt Integration",
    summary: "Checks whether i64 values round-trip through JavaScript as BigInt.",
    group_id: "wasm_feature",
    execution_kind: "async_local"
  }),
  define_js_probe({
    id: "streaming_compilation",
    title: "Streaming Compilation",
    summary: "Checks for WebAssembly.compileStreaming support.",
    group_id: "wasm_feature",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "exception_tag_api",
    title: "Exception Tag API",
    summary: "Checks for the JavaScript WebAssembly.Tag surface.",
    group_id: "wasm_feature",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "exceptions_final",
    title: "Exceptions Final",
    summary:
      "Checks whether the finalized exception-handling binary with exnref compiles.",
    group_id: "wasm_feature",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "jspi_js_api",
    title: "JSPI JS API",
    summary: "Checks for JavaScript Promise Integration helpers.",
    group_id: "wasm_feature",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "js_string_builtins",
    title: "JS String Builtins",
    summary:
      "Checks whether WebAssembly.instantiate accepts js-string builtins.",
    group_id: "wasm_feature",
    execution_kind: "async_local"
  }),
  define_js_probe({
    id: "type_reflection",
    title: "Type Reflection",
    summary: "Checks for WebAssembly.Function type reflection support.",
    group_id: "wasm_feature",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "hardware_concurrency",
    title: "Hardware Concurrency",
    summary: "Captures navigator.hardwareConcurrency for thread sizing.",
    group_id: "wasm_feature",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "max_memory_4gb",
    title: "4GB Memory Ceiling",
    summary: "Checks whether the runtime accepts a 4GB maximum memory size.",
    group_id: "memory",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "indexeddb_usable",
    title: "IndexedDB Usable",
    summary: "Confirms IndexedDB opens successfully instead of just existing.",
    group_id: "storage",
    execution_kind: "async_local"
  }),
  define_js_probe({
    id: "opfs_available",
    title: "OPFS Available",
    summary: "Checks for Origin Private File System directory access.",
    group_id: "storage",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "opfs_sync_access",
    title: "OPFS Sync Access",
    summary: "Checks for synchronous OPFS handles where supported.",
    group_id: "storage",
    execution_kind: "async_local"
  }),
  define_js_probe({
    id: "storage_persist",
    title: "Storage Persist",
    summary: "Checks whether StorageManager.persist is exposed.",
    group_id: "storage",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "storage_estimate",
    title: "Storage Estimate",
    summary: "Checks whether StorageManager.estimate is exposed.",
    group_id: "storage",
    execution_kind: "async_local"
  }),
  define_js_probe({
    id: "cache_api",
    title: "Cache API",
    summary: "Checks for the caches API in the current context.",
    group_id: "storage",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "webtransport_ctor",
    title: "WebTransport Constructor",
    summary: "Checks whether the WebTransport constructor exists.",
    group_id: "networking",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "websocket_ctor",
    title: "WebSocket Constructor",
    summary: "Checks for WebSocket availability.",
    group_id: "networking",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "connection_info",
    title: "Connection Info",
    summary:
      "Captures Network Information API hints such as RTT and downlink.",
    group_id: "networking",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "webrtc_peer_connection",
    title: "RTCPeerConnection",
    summary: "Checks for WebRTC peer connection support.",
    group_id: "networking",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "webrtc_trickle_ice",
    title: "WebRTC Trickle ICE",
    summary:
      "Checks whether ICE candidates gather incrementally during offer setup.",
    group_id: "networking",
    execution_kind: "async_local"
  }),
  define_js_probe({
    id: "rtc_data_channel",
    title: "RTC Data Channel",
    summary: "Confirms that a data channel can be created.",
    group_id: "networking",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "rtc_data_channel_loopback",
    title: "RTC Data Channel Loopback",
    summary:
      "Checks whether a local RTC data channel can open and echo a message.",
    group_id: "networking",
    execution_kind: "async_local"
  }),
  define_js_probe({
    id: "fetch_streaming_request",
    title: "Fetch Streaming Request",
    summary: "Checks for ReadableStream request-body support.",
    group_id: "networking",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "readable_stream_async_iterator",
    title: "ReadableStream Async Iterator",
    summary:
      "Checks whether ReadableStream implements Symbol.asyncIterator.",
    group_id: "networking",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "abort_signal_any",
    title: "AbortSignal.any",
    summary: "Checks for AbortSignal.any support.",
    group_id: "networking",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "abort_signal_timeout",
    title: "AbortSignal.timeout",
    summary: "Checks for AbortSignal.timeout support.",
    group_id: "networking",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "compression_streams",
    title: "Compression Streams",
    summary: "Checks for CompressionStream availability.",
    group_id: "networking",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "dedicated_worker",
    title: "Dedicated Worker",
    summary: "Checks whether Worker is available.",
    group_id: "threading",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "shared_worker_ctor",
    title: "Shared Worker Constructor",
    summary: "Checks whether SharedWorker is exposed.",
    group_id: "threading",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "module_worker",
    title: "Module Worker",
    summary: "Checks whether module workers can be started.",
    group_id: "threading",
    execution_kind: "async_local"
  }),
  define_js_probe({
    id: "atomics_wait_async",
    title: "Atomics.waitAsync",
    summary: "Checks for non-blocking atomic wait support.",
    group_id: "threading",
    execution_kind: "sync",
    requires_cross_origin_isolation: true
  }),
  define_js_probe({
    id: "message_channel",
    title: "MessageChannel",
    summary: "Checks for MessageChannel support.",
    group_id: "threading",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "transferable_streams",
    title: "Transferable Streams",
    summary: "Checks whether ReadableStream objects can be transferred.",
    group_id: "threading",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "wasm_in_worker",
    title: "WASM In Worker",
    summary: "Checks whether a worker can instantiate a wasm module.",
    group_id: "threading",
    execution_kind: "async_local"
  }),
  define_js_probe({
    id: "web_locks_usable",
    title: "Web Locks",
    summary: "Checks whether navigator.locks.request succeeds.",
    group_id: "threading",
    execution_kind: "async_local"
  }),
  define_js_probe({
    id: "scheduler_post_task",
    title: "scheduler.postTask",
    summary: "Checks for scheduler.postTask support.",
    group_id: "threading",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "request_idle_callback",
    title: "requestIdleCallback",
    summary: "Checks for idle callback scheduling support.",
    group_id: "threading",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "subtle_crypto",
    title: "SubtleCrypto",
    summary: "Checks for crypto.subtle availability.",
    group_id: "crypto",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "ed25519_generate_key",
    title: "Ed25519",
    summary: "Checks whether Ed25519 key generation succeeds.",
    group_id: "crypto",
    execution_kind: "async_local"
  }),
  define_js_probe({
    id: "x25519_generate_key",
    title: "X25519",
    summary: "Checks whether X25519 key generation succeeds.",
    group_id: "crypto",
    execution_kind: "async_local"
  }),
  define_js_probe({
    id: "csprng",
    title: "CSPRNG",
    summary: "Checks for crypto.getRandomValues support.",
    group_id: "crypto",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "text_encoder",
    title: "TextEncoder",
    summary: "Checks for TextEncoder availability.",
    group_id: "encoding",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "encode_into",
    title: "TextEncoder.encodeInto",
    summary: "Checks for the zero-copy encodeInto fast path.",
    group_id: "encoding",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "structured_clone",
    title: "structuredClone",
    summary: "Checks for structuredClone availability.",
    group_id: "encoding",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "service_worker_api",
    title: "Service Worker API",
    summary: "Checks for navigator.serviceWorker exposure.",
    group_id: "platform",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "broadcast_channel",
    title: "BroadcastChannel",
    summary: "Checks whether BroadcastChannel can be created.",
    group_id: "platform",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "battery_status",
    title: "Battery Status",
    summary: "Checks whether navigator.getBattery returns charging state.",
    group_id: "platform",
    execution_kind: "async_local"
  }),
  define_js_probe({
    id: "page_lifecycle",
    title: "Page Lifecycle",
    summary: "Checks for freeze lifecycle handlers.",
    group_id: "platform",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "storage_event",
    title: "Storage Event",
    summary: "Checks for StorageEvent availability.",
    group_id: "platform",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "notification_api",
    title: "Notification API",
    summary: "Checks for Notification availability.",
    group_id: "platform",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "push_manager",
    title: "Push Manager",
    summary: "Checks for PushManager availability.",
    group_id: "platform",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "execution_context",
    title: "Execution Context",
    summary: "Classifies the current execution environment.",
    group_id: "platform",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "dynamic_import",
    title: "Dynamic Import",
    summary: "Checks whether dynamic import syntax parses.",
    group_id: "platform",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "import_maps",
    title: "Import Maps",
    summary: "Checks whether import maps are supported.",
    group_id: "platform",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "memory_measurement",
    title: "User-Agent Specific Memory",
    summary: "Checks for measureUserAgentSpecificMemory support.",
    group_id: "performance",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "performance_observer",
    title: "PerformanceObserver",
    summary: "Checks for PerformanceObserver support.",
    group_id: "performance",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "resize_observer",
    title: "ResizeObserver",
    summary: "Checks for ResizeObserver support.",
    group_id: "performance",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "intersection_observer",
    title: "IntersectionObserver",
    summary: "Checks for IntersectionObserver support.",
    group_id: "performance",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "reporting_observer",
    title: "ReportingObserver",
    summary: "Checks for ReportingObserver support.",
    group_id: "performance",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "memory_pressure_signal",
    title: "Memory Pressure Signal",
    summary:
      "Checks for memory pressure events or memory pressure measurement APIs.",
    group_id: "performance",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "high_resolution_time",
    title: "High Resolution Time",
    summary: "Checks for performance.now availability.",
    group_id: "performance",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "array_buffer_transfer",
    title: "ArrayBuffer.transfer",
    summary: "Checks whether ArrayBuffer.transfer exists.",
    group_id: "memory",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "resizable_array_buffer",
    title: "Resizable ArrayBuffer",
    summary: "Checks whether resizable ArrayBuffers can be created and resized.",
    group_id: "memory",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "weak_ref",
    title: "WeakRef",
    summary: "Checks for WeakRef support.",
    group_id: "memory",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "finalization_registry",
    title: "FinalizationRegistry",
    summary: "Checks for FinalizationRegistry support.",
    group_id: "memory",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "device_memory",
    title: "Device Memory",
    summary: "Captures navigator.deviceMemory when available.",
    group_id: "memory",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "storage_quota",
    title: "Storage Quota",
    summary: "Captures usage and quota estimates.",
    group_id: "memory",
    execution_kind: "async_local"
  }),
  define_js_probe({
    id: "offscreen_canvas",
    title: "OffscreenCanvas",
    summary: "Checks for OffscreenCanvas availability.",
    group_id: "graphics",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "offscreen_canvas_2d",
    title: "OffscreenCanvas 2D",
    summary: "Checks whether OffscreenCanvas can produce a 2D context.",
    group_id: "graphics",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "offscreen_canvas_webgl",
    title: "OffscreenCanvas WebGL",
    summary: "Checks whether OffscreenCanvas can produce a WebGL context.",
    group_id: "graphics",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "webgpu",
    title: "WebGPU",
    summary: "Checks whether navigator.gpu is available.",
    group_id: "graphics",
    execution_kind: "async_local"
  }),
  define_js_probe({
    id: "webgl",
    title: "WebGL",
    summary: "Checks whether WebGL or WebGL2 contexts can be created.",
    group_id: "graphics",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "webauthn",
    title: "WebAuthn",
    summary: "Checks for PublicKeyCredential availability.",
    group_id: "auth",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "platform_authenticator",
    title: "Platform Authenticator",
    summary: "Checks whether a platform authenticator is available.",
    group_id: "auth",
    execution_kind: "async_local"
  }),
  define_js_probe({
    id: "conditional_mediation",
    title: "Conditional Mediation",
    summary: "Checks whether passkey conditional mediation is available.",
    group_id: "auth",
    execution_kind: "async_local"
  }),
  define_js_probe({
    id: "credential_management",
    title: "Credential Management",
    summary: "Checks for navigator.credentials exposure.",
    group_id: "auth",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "video_decoder",
    title: "VideoDecoder",
    summary: "Checks for VideoDecoder availability.",
    group_id: "media",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "video_decoder_codec_support",
    title: "VideoDecoder Codec Support",
    summary:
      "Checks WebCodecs decoder support for H.264, VP9, and AV1 test configs.",
    group_id: "media",
    execution_kind: "async_local"
  }),
  define_js_probe({
    id: "video_encoder",
    title: "VideoEncoder",
    summary: "Checks for VideoEncoder availability.",
    group_id: "media",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "video_encoder_codec_support",
    title: "VideoEncoder Codec Support",
    summary:
      "Checks WebCodecs encoder support for H.264, VP9, and AV1 test configs.",
    group_id: "media",
    execution_kind: "async_local"
  }),
  define_js_probe({
    id: "audio_decoder",
    title: "AudioDecoder",
    summary: "Checks for AudioDecoder availability.",
    group_id: "media",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "audio_decoder_codec_support",
    title: "AudioDecoder Codec Support",
    summary: "Checks WebCodecs decoder support for an Opus audio config.",
    group_id: "media",
    execution_kind: "async_local"
  }),
  define_js_probe({
    id: "audio_encoder",
    title: "AudioEncoder",
    summary: "Checks for AudioEncoder availability.",
    group_id: "media",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "audio_encoder_codec_support",
    title: "AudioEncoder Codec Support",
    summary: "Checks WebCodecs encoder support for an Opus audio config.",
    group_id: "media",
    execution_kind: "async_local"
  }),
  define_js_probe({
    id: "clone_array_buffer",
    title: "Clone ArrayBuffer",
    summary: "Checks whether ArrayBuffer survives structuredClone.",
    group_id: "transfer",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "clone_shared_array_buffer",
    title: "Clone SharedArrayBuffer",
    summary: "Checks whether SharedArrayBuffer survives structuredClone.",
    group_id: "transfer",
    execution_kind: "sync",
    requires_cross_origin_isolation: true
  }),
  define_js_probe({
    id: "clone_readable_stream",
    title: "Clone ReadableStream",
    summary: "Checks whether ReadableStream survives structuredClone.",
    group_id: "transfer",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "transferable_array_buffer",
    title: "Transfer ArrayBuffer",
    summary: "Checks whether ArrayBuffer can be transferred.",
    group_id: "transfer",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "transferable_message_port",
    title: "Transfer MessagePort",
    summary: "Checks whether MessagePort can be transferred.",
    group_id: "transfer",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "transferable_offscreen_canvas",
    title: "Transfer OffscreenCanvas",
    summary: "Checks whether OffscreenCanvas can be transferred.",
    group_id: "transfer",
    execution_kind: "sync"
  }),
  define_js_probe({
    id: "transferable_readable_stream",
    title: "Transfer ReadableStream",
    summary: "Checks whether ReadableStream can be transferred.",
    group_id: "transfer",
    execution_kind: "sync"
  })
] as const;

/**
 * Full ordered probe manifest.
 */
export const PROBE_MANIFEST: readonly ProbeDefinition[] = [
  ...SMOKE_PROBES,
  ...WAT_PROBES,
  ...JS_PROBES
] as const;

/**
 * Probe manifest lookup table by identifier.
 */
export const PROBE_MANIFEST_BY_ID: ReadonlyMap<string, ProbeDefinition> = new Map(
  PROBE_MANIFEST.map((probe): readonly [string, ProbeDefinition] => [
    probe.id,
    probe
  ])
);

/**
 * Returns the ordered probe list for the standard probe plan.
 */
export function get_probe_plan(): readonly ProbeDefinition[] {
  return PROBE_MANIFEST.filter((probe): boolean => probe.default_enabled);
}

/**
 * Looks up a probe definition by identifier.
 */
export function get_probe_definition(
  probe_id: string
): ProbeDefinition | undefined {
  return PROBE_MANIFEST_BY_ID.get(probe_id);
}

/**
 * Narrows a probe definition to a WAT-backed probe.
 */
export function is_wat_probe(
  probe: ProbeDefinition
): probe is WatProbeDefinition {
  return probe.implementation_kind === "wat_validate";
}
